import { useRef } from 'react';
import ResultSelectOverlay from './ResultSelectOverlay';
import type { ResultSelectionControls } from './resultSelection';

/** In-flow checkbox shown once a selection is active, so every result can be toggled without hovering. */
export function ResultSelectionControl({
  selection,
  recordId,
  title,
}: {
  selection: ResultSelectionControls;
  recordId: string;
  title: string;
}) {
  if (!selection.enabled || !selection.active) return null;

  return (
    <ResultCheckbox
      checked={selection.isSelected(recordId)}
      label={`Select ${title}`}
      onToggle={(shiftKey) => selection.onToggle(recordId, shiftKey)}
    />
  );
}

/** Empty checkbox in the hover action cluster. Hidden once a selection is already active. */
export function ResultSelectHover({
  selection,
  recordId,
  title,
  className,
}: {
  selection: ResultSelectionControls;
  recordId: string;
  title: string;
  className: string;
}) {
  if (!selection.enabled || selection.active) return null;

  return (
    <ResultSelectOverlay
      label={`Select ${title}`}
      onToggle={(shiftKey) => selection.onToggle(recordId, shiftKey)}
      className={className}
    />
  );
}

/** Native checkbox used in the left gutter after selection starts. */
export default function ResultCheckbox({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: string;
  onToggle: (shiftKey: boolean) => void;
}) {
  const shiftKeyRef = useRef(false);

  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      onMouseDown={(event) => {
        shiftKeyRef.current = event.shiftKey;
      }}
      onKeyDown={(event) => {
        shiftKeyRef.current = event.shiftKey;
      }}
      onClick={(event) => {
        event.stopPropagation();
        shiftKeyRef.current = event.shiftKey;
      }}
      onChange={() => onToggle(shiftKeyRef.current)}
      className="h-4 w-4 shrink-0 accent-emerald-700 cursor-pointer"
    />
  );
}
